import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Loading from '../components/common/Loading';
import { getBlogPost, getBlogPosts } from '../lib/api';

const BlogDetail = () => {
  const params = useParams();
  const [blog, setBlog] = useState(null);
  const [otherBlogs, setOtherBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBlog = useCallback(async () => {
    try {
      const data = await getBlogPost(params.slug);
      setBlog(data);
    } catch (error) {
      console.error('Error fetching blog:', error);
      setBlog(null);
    } finally {
      setLoading(false);
    }
  }, [params.slug]);

  const fetchOtherBlogs = useCallback(async () => {
    try {
      const data = await getBlogPosts({
        published: true,
        excludeSlug: params.slug,
        limit: 5,
      });
      setOtherBlogs(data || []);
    } catch (error) {
      console.error('Error fetching other blogs:', error);
    }
  }, [params.slug]);

  useEffect(() => {
    fetchBlog();
    fetchOtherBlogs();
  }, [fetchBlog, fetchOtherBlogs]);

  if (loading) {
    return <Loading message="Blog yazısı yükleniyor..." />;
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Blog yazısı bulunamadı</h2>
          <p className="text-gray-600 mb-4">
            Aradığınız blog yazısı mevcut değil veya kaldırılmış olabilir.
          </p>
          <Link to="/blogs" className="text-[#dbc582] hover:text-[#c8bca4] transition-colors">
            Blog listesine dön
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gray-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="lg:grid lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-9">
            {blog.image_url && (
              <div className="aspect-w-16 aspect-h-9 mb-8">
                <img
                  src={blog.image_url}
                  alt={blog.title}
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            )}
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{blog.title}</h1>
            <div className="text-sm text-gray-500 mb-8">
              {new Date(blog.created_at).toLocaleDateString('tr-TR')}
            </div>
            <div
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-[#dbc582] prose-strong:text-gray-900 prose-table:border-collapse prose-th:border prose-th:border-gray-300 prose-th:p-2 prose-td:border prose-td:border-gray-300 prose-td:p-2"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </div>

          <div className="lg:col-span-3 mt-12 lg:mt-0">
            <div className="sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Diğer Blog Yazıları</h2>
              <div className="space-y-4">
                {otherBlogs.map((otherBlog) => (
                  <Link key={otherBlog.id} to={`/blogs/${otherBlog.slug}`} className="group block">
                    <div className="flex items-center">
                      {otherBlog.image_url && (
                        <div className="flex-shrink-0">
                          <img
                            src={otherBlog.image_url}
                            alt={otherBlog.title}
                            className="h-14 w-14 object-cover rounded"
                          />
                        </div>
                      )}
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900 group-hover:text-[#dbc582] line-clamp-2">
                          {otherBlog.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(otherBlog.created_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
                <Link
                  to="/blogs"
                  className="inline-flex items-center text-sm font-medium text-[#dbc582] hover:text-[#c8bca4]"
                >
                  Tüm Blog Yazıları
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogDetail;
